import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
    FlyFreelyError,
    FlyFreelyLoggingService,
    MissionOverlapAcknowledgementDto,
    MissionOverlapDto,
    MissionService,
    SimpleMissionDto,
    UserService,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { FeatureGroup } from '@flyfreely-portal-ui/map';
import moment from 'moment/moment';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'mission-overlap-details',
    templateUrl: './mission-overlap-details.component.html'
})
export class MissionOverlapDetailsComponent implements OnInit {
    @Input() overlappingMission: SimpleMissionDto;
    @Input() missionId: number;
    @Input() isAcknowledged = false;
    @Input() organisationId: number;
    @Input() locationFeatures: FeatureGroup[] = [];
    @Input() overlapStatus: MissionOverlapDto.Status = MissionOverlapDto.Status.APPLICABLE;
    @Output() acknowledge =
        new EventEmitter<MissionOverlapAcknowledgementDto>();

    allFeatures: FeatureGroup[] = [];

    private workTracker = new WorkTracker();
    working = false;
    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private modal: BsModalRef<MissionOverlapDetailsComponent>,
        private missionService: MissionService,
        private userService: UserService,
        private logging: FlyFreelyLoggingService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));
    }

    ngOnInit() {
        const existingFeatures = this.overlappingMission?.locationBoundary
            ? [
                  {
                      id: 1000,
                      name: this.overlappingMission.name,
                      geom: this.overlappingMission?.locationBoundary
                  }
              ]
            : [];

        const defaultFeatures: FeatureGroup[] = [
            {
                canAdd: false,
                id: 1000,
                name: 'Area',
                type: 'Polygon',
                existingFeatures,
                styles: {
                    symbol: [
                        {
                            layout: {
                                'text-field': '{name}',
                                'symbol-placement': 'line',
                                'text-anchor': 'bottom',
                                'symbol-spacing': 150,
                                'text-max-angle': 35,
                                'text-size': 12,
                                'text-letter-spacing': 0.1,
                                'text-font': [
                                    'Roboto Bold',
                                    'Arial Unicode MS Regular'
                                ]
                            },
                            paint: {
                                'text-halo-color': 'hsl(0, 100%, 100%)',
                                'text-halo-width': 1,
                                'text-halo-blur': 1
                            }
                        }
                    ]
                }
            } as FeatureGroup
        ];
        this.allFeatures = [...this.locationFeatures, ...defaultFeatures];
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    get isInformational(): boolean {
        return this.overlapStatus === MissionOverlapDto.Status.INFORMATIONAL;
    }

    onCancel() {
        this.modal.hide();
    }

    onAcknowledge() {
        this.missionService
            .acknowledgeOverlap(this.missionId, this.overlappingMission.id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: () => {
                    this.isAcknowledged = true;
                    const acknowledgement: MissionOverlapAcknowledgementDto = {
                        personId: this.userService.getCurrentUser().id,
                        acknowledgementTime: moment(new Date())
                            .utc()
                            .toISOString()
                    };
                    this.acknowledge.emit(acknowledgement);
                    this.logging.success(
                        `Successfully acknowledged conflicting mission`
                    );
                    this.modal.hide();
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error acknowledging mission overlap: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }
}
